import axios from 'axios';
import { SERVER_URL } from '../App';
import { debugLog, devLog } from '../utils/debugLog';
import GenConfig from '../core/GenConfig';
import { trySolveLocally, isLocalSolveAvailable } from '../core/localSolve';
import { peekItemCatalog } from './itemCatalog';
import { hydrateBuildLocally } from '../core/localSolve/hydrateBuild';
import { magingElementForDamageId } from '../core/weaponMaging';
import { MAGABLE_SLOTS } from '../core/itemMaging';

const REQ_KEY_CURRENT_BUILD = 'initial_build';

/** Pause before retrying the hydrate that turns a finished local solve into a build. */
const HYDRATE_RETRY_MS = 400;
/**
 * Floor between live build previews during a solve. Each one is a hydrate, and
 * the pool finds a new incumbent far more often than a person can read one.
 */
const LIVE_PREVIEW_MS = 600;
const REQ_KEY_ITEMS = 'items';
/** Longest decline reason sent with a report; the server keeps a short note. */
const FALLBACK_REASON_MAX = 200;

/**
 * Build a typed Error for a 429 generation quota response.
 * `code` is 'LOGIN_REQUIRED' (anon free-tier cap) or 'DAILY_QUOTA' (logged-in cap).
 */
function quotaError(data) {
    const limitType = data?.limit_type;
    const err = new Error(data?.error || 'Generation limit reached');
    err.status = 429;
    err.code = limitType === 'login_required' ? 'LOGIN_REQUIRED' : 'DAILY_QUOTA';
    err.period = data?.period;
    err.retryAfterSeconds = data?.retry_after_seconds;
    err.limit = data?.limit;
    return err;
}

/**
 * The error for a `gen/authorize` that got no answer, or a 5xx one. Not a
 * decline either: the browser can solve, the server cannot be asked, and
 * telling the user their browser is at fault sends them the wrong way.
 */
function serverDownError(axiosErr) {
    const status = axiosErr?.response?.status;
    const err = new Error(axiosErr?.message || 'Network Error');
    err.serverDown = true;
    if (status) err.status = status;
    else err.networkError = true;
    return err;
}

function serializeCurrentBuild(build) {
    if (!build || !Array.isArray(build.items)) {
        return { [REQ_KEY_ITEMS]: [] };
    }
    const items = build.items.map((item) =>
        item && item.id != null ? Number(item.id) : null
    );
    return { [REQ_KEY_ITEMS]: items };
}

/**
 * Builds the request payload for the generator (`stats_criteria`, `spells_criteria`, `weapon_criteria`, etc.).
 * @param {Build} build
 * @param {GenConfig} genConfig
 * @param {number} DEFAULT_LVL
 * @returns {{ req: object, timeLimit: number }}
 */
function buildGenRequest(build, genConfig, DEFAULT_LVL) {
    const statsCriteria = build.getSolverTargets();

    const settings = genConfig.getSettings();
    const lockedItems = {};
    Object.keys(settings.lockedSlots || {}).forEach((slotId) => {
        const idx = Number(slotId);
        const currentItem = Array.isArray(build?.items) ? build.items[idx] : null;
        if (currentItem && typeof currentItem === 'object') {
            lockedItems[slotId] = currentItem;
        }
    });

    const currentBuild = serializeCurrentBuild(build);
    const spellsCriteria =
        typeof build.getMaximizeSpellCriteria === 'function'
            ? build.getMaximizeSpellCriteria()
            : (typeof build.getMaximizeSpellIds === 'function' ? build.getMaximizeSpellIds() : []);
    const weaponItem = typeof build?.getWeapon === 'function' ? build.getWeapon() : null;
    const weaponId =
        weaponItem?.id != null && Number.isFinite(Number(weaponItem.id))
            ? Number(weaponItem.id)
            : null;
    const weaponMaging =
        typeof build?.getWeaponMaging === 'function' ? build.getWeaponMaging() : null;

    let weaponCriteriaPayload = {};
    if (build?.maximizeWeapon === true && weaponId != null) {
        weaponCriteriaPayload = weaponMaging
            ? {
                item_id: weaponId,
                maging_element: weaponMaging.damage,
                maging_heal_element: weaponMaging.heal,
            }
            : weaponId;
    }

    const req = {
        lvl: build?.lvl || DEFAULT_LVL,
        stats_criteria: statsCriteria,
        spells_criteria: spellsCriteria,
        weapon_criteria: weaponCriteriaPayload,
        defense_share: genConfig?.defenseShare || 0,
        // hasScrolls lives on the Build (the stats panel's scroll toggle writes
        // it), not in genConfig, so it is stitched in here rather than coming
        // out of getSettings() — the solver otherwise always assumes +100 per
        // characteristic and scores a character the stats panel is not showing.
        settings: {
            ...settings,
            hasScrolls: typeof build?.isFullyScrolled === 'function' ? build.isFullyScrolled() : true,
            locked_items: lockedItems,
        },
        // The exos the run places, +1 each, as the kernel request counts them.
        exos: Object.fromEntries((build?.fmExos || []).map((id) => [id, 1])),
        [REQ_KEY_CURRENT_BUILD]: currentBuild,
    };

    return { req, timeLimit: GenConfig.searchBudgetSeconds(genConfig.timeLimit) };
}

/**
 * Solve in the browser — the only solver there is.
 *
 * Returns the kernel result, or null when the browser declines the request; the
 * reason goes to `opts.onFallback`. The one thing that throws is the generation
 * cap (a typed quota error from `authorizeLocalSolve`): that is not a decline,
 * and the caller shows the cap-gate for it.
 */
export async function attemptLocalSolve(build, genConfig, opts) {
    const fellBack = (reason) => {
        debugLog('Local solve declined:', reason);
        if (typeof opts?.onFallback === 'function') opts.onFallback(reason);
        return null;
    };
    if (!isLocalSolveAvailable()) return fellBack('local solve unavailable');
    const outcome = await trySolveLocally(build, genConfig, opts);

    if (!outcome.solved) return fellBack(outcome.reason);
    debugLog('Local solve done:', {
        islands: outcome.islands,
        ms: outcome.ms,
        prepareMs: outcome.prepareMs,
        searchMs: outcome.searchMs,
        rounds: outcome.rounds,
        evals: outcome.evals,
        // Aggregate and per-island throughput: the gap between them is how well
        // the islands actually run in parallel.
        evalsPerSec: outcome.searchMs ? Math.round(outcome.evals / (outcome.searchMs / 1000)) : null,
        perIslandPerSec: outcome.searchMs && outcome.islands
            ? Math.round(outcome.evals / (outcome.searchMs / 1000) / outcome.islands)
            : null,
    });
    return outcome;
}

/**
 * Solve `build` in the browser and hydrate the winner into the payload the UI
 * renders — the whole of a generation, for any page that starts one.
 *
 * Resolves `{ result: { build, score } }`, or null when the browser declines
 * (the reason goes to `onFallback`). Throws the typed quota error when the
 * generation cap is reached, and whatever hydrating the winner throws after
 * its retry.
 *
 * Optional hooks: `onStart`/`onSearching`/`onFinalize`/`onProgress` as
 * `trySolveLocally` has them; `onPreview(payload)` with a hydrated best-so-far
 * now and then; `onReport(report)` with the finished run's report, awaited
 * before the final hydrate so it is on screen while that is in flight.
 */
export async function solveBuild(build, genConfig, {
    seconds = GenConfig.TIME_LIMIT_DEFAULT,
    signal,
    onFallback,
    onStart,
    onSearching,
    onFinalize,
    onProgress,
    onPreview,
    onReport,
} = {}) {
    // The permit authorize buys for this run; hydrate has to present it.
    let solveToken = null;
    // What the run would forge the weapon it is holding to. Applied to the
    // previews as well as the final build: each preview swaps the weapon, and
    // without this the card kept drawing the previous weapon's element.
    // The transcendence runes it chose, where the exos went, and the slots it
    // was free to use — every mageable slot the user did not lock — so what an
    // earlier run put there and this one did not is removed rather than kept.
    // Sent with runes switched off too: that run scored those slots without them.
    const lockedSlots = new Set(Object.keys(genConfig?.getSettings?.().lockedSlots || {}).map(Number));
    const runeSlots = MAGABLE_SLOTS.filter((slot) => !lockedSlots.has(slot));
    const withMaging = (payload, result) => {
        if (payload && build?.maximizeBestWeapon === true) {
            payload.weapon_maging_element = magingElementForDamageId(result?.weapon_maging_damage_id);
        }
        if (payload) {
            payload.maging = { runes: result?.runes || [], exos: result?.exos || [], slots: runeSlots };
        }
        return payload;
    };

    let lastShown = '';
    let lastShownAt = 0;
    const showPreview = async (preview) => {
        if (typeof onPreview !== 'function' || !preview?.items) return;
        const key = preview.items.join(',');
        const now = Date.now();
        if (key === lastShown || now - lastShownAt < LIVE_PREVIEW_MS) return;
        lastShown = key;
        lastShownAt = now;
        try {
            const payload = await service.hydrateBuild(preview.items, preview.invested, solveToken);
            if (payload) onPreview(withMaging(payload, preview));
        } catch (_err) {
            // A preview is decoration; the final build still lands.
        }
    };

    const outcome = await attemptLocalSolve(build, genConfig, {
        seconds,
        // Pressing stop has to reach the workers, or they search on and the
        // next generation stacks another set on top.
        signal,
        onFallback,
        // Only called once the request is known to be solvable, so nothing that
        // is about to decline spends a generation from the cap.
        authorize: async (shaping) => {
            const permit = await service.authorizeLocalSolve(shaping);
            solveToken = permit?.token || null;
            return permit;
        },
        onStart,
        onSearching,
        onFinalize,
        onProgress: (progress) => {
            if (typeof onProgress === 'function') onProgress(progress);
            showPreview(progress?.preview);
        },
    });
    if (!outcome?.result) return null;
    if (typeof onReport === 'function') await onReport(outcome.report);

    // Names, icons and set bonuses are not in the packed dataset. Retried,
    // unlike the previews: losing this one throws away a finished search.
    const { items, invested, soft } = outcome.result;
    const payload = await service.hydrateBuild(items, invested, solveToken, { retries: 1 });
    if (!payload) {
        if (typeof onFallback === 'function') onFallback('build could not be hydrated');
        return null;
    }
    // Set even when the answer is "as forged" (null): the run chose the weapon,
    // so its answer replaces whatever the build carried — otherwise an element
    // left over from an earlier run survives one that decided against it.
    withMaging(payload, outcome.result);
    devLog('[Gen] result', { solved: outcome.result, report: outcome.report, build: payload });
    return { result: { build: payload, score: soft } };
}

class GenService {

    /**
     * Turn a locally solved item list into the payload the UI renders.
     *
     * The kernel returns 16 item ids and 6 invested points; names, icons and
     * set bonuses come from the item catalog, or from `gen/hydrate` when the
     * catalog has not loaded — the same payload shape either way.
     *
     * @param {number[]} items 16 item ids in slot order, 0 for an empty slot
     * @param {number[]} investedPoints 6 values, ATTRIBUTES order
     * @returns {Promise<object|null>} build payload, or null if it cannot be built
     */
    async hydrateBuild(items, investedPoints, token, { retries = 0 } = {}) {
        // The catalog carries the names, icons and set bonuses this used to make
        // a round trip for — one per generation, and several more per run while
        // interim previews stream in. hydrateBuildLocally is a port of the same
        // serialize_build, pinned to it by hydrateParity.test.js.
        const catalog = peekItemCatalog();
        if (catalog) {
            try {
                const payload = hydrateBuildLocally(catalog, items, investedPoints);
                // Logged like the search paths are. Without it the local route is
                // invisible when it works and only speaks up when it fails, which
                // is the wrong way round for the one path with no request to watch.
                debugLog('[Hydrate] Local', {
                    version: catalog.version,
                    equipped: items.filter(Boolean).length,
                    sets: Object.keys(payload.set_bonuses).length,
                });
                return payload;
            } catch (error) {
                console.warn('[hydrate] local hydrate failed, using the server:', error);
            }
        }

        let lastErr = null;
        for (let attempt = 0; attempt <= retries; attempt += 1) {
            try {
                const res = await axios.post(
                    `${SERVER_URL()}gen/hydrate`,
                    { items, invested_points: investedPoints, token },
                    { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
                );
                if (res.data?.build) return res.data.build;
            } catch (err) {
                lastErr = err;
            }
            // Worth one more try: the search is already done and paid for, and
            // failing here throws it away.
            if (attempt < retries) {
                await new Promise((r) => setTimeout(r, HYDRATE_RETRY_MS));
            }
        }
        if (lastErr) throw lastErr;
        return null;
    }

    /**
     * Names for the ids a locally solved generation report is made of.
     *
     * The kernel knows items only as integers, and the report lists far more of
     * them than the build contains — every candidate the search could have
     * drawn. Resolving them here keeps the report readable without shipping an
     * item name table into the bundle.
     *
     * Returns empty maps on failure: the report is decoration, and it degrades
     * to showing ids rather than disappearing.
     */
    async resolveSolveNames(itemIds, setIds) {
        try {
            const res = await axios.post(
                `${SERVER_URL()}gen/solve-names`,
                { item_ids: itemIds, set_ids: setIds },
                { headers: { 'Content-Type': 'application/json' } },
            );
            return { items: res.data?.items || {}, sets: res.data?.sets || {} };
        } catch (_err) {
            return { items: {}, sets: {} };
        }
    }

    /**
     * Ask permission to solve in the browser, which counts against the
     * generation cap.
     *
     * Returns the permit, or null when it is refused. A reached cap throws the
     * typed quota error instead: the caller shows the cap-gate for it. A server
     * that cannot be reached throws `serverDownError`, for the same reason. It used
     * to return null too, back when a refusal fell through to a server path
     * that re-ran the check and raised the gate — once that path only answered
     * "no solver", a capped visitor was told their browser could not generate.
     */
    async authorizeLocalSolve(shaping) {
        let res;
        try {
            res = await axios.post(
                `${SERVER_URL()}gen/authorize`,
                shaping || {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    validateStatus: (s) => s === 200 || s === 429,
                },
            );
        } catch (err) {
            if (!err?.response || err.response.status >= 500) throw serverDownError(err);
            return null;
        }
        if (res.status === 429) throw quotaError(res.data);
        if (res.status !== 200 || !res.data?.token) return null;
        // Every field the caller acts on has to be copied across: this object
        // *is* the permit as far as trySolveLocally is concerned, and a field
        // dropped here disables the check that reads it, silently and with its
        // own tests still passing.
        return {
            token: res.data.token,
            spell_criteria: res.data.spell_criteria || [],
            stat_overrides: res.data.stat_overrides || [],
            // Null while the server's background pack has not settled, which
            // the caller must read as "proceed" — never defaulted here.
            dataset_digest: res.data.dataset_digest,
            // Left undefined when the server did not send it, so the caller
            // can tell "no legendaries" from "this server cannot say".
            legendary_item_ids: res.data.legendary_item_ids,
            // Same rule: undefined means this server has no find-the-best-
            // weapon pool to give, which is a server too old for the
            // criterion rather than a pool that came back empty.
            best_weapon: res.data.best_weapon,
        };
    }

    /**
     * Tell the server the browser declined this generation, and why.
     *
     * Nothing solves on the server. `POST gen/` refuses every request and
     * records the reason, which is what the dashboard's fallback series is made
     * of — the reason exists only here. Fire-and-forget: the answer is always
     * no, and a report that fails to send must not change what the user sees.
     *
     * @param {object} req - from `buildGenRequest`; the server reads its level
     *   and criteria count for the activity feed
     * @param {string} [reason] - why the browser declined
     */
    reportLocalDecline(req, reason) {
        const body = { req };
        if (reason) body.fallback_reason = String(reason).slice(0, FALLBACK_REASON_MAX);
        axios.post(`${SERVER_URL()}gen/`, body, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
            validateStatus: () => true,
        }).catch(() => {});
    }

}

const service = new GenService();
service.buildGenRequest = buildGenRequest;
export default service;
